import "./GuidePage.css";

function GuideImage({ src, alt, caption }) {
  return (
    <div className="guide-img-wrap">
      <img src={src} alt={alt} className="guide-img" />
      {caption && <p className="guide-img-caption">{caption}</p>}
    </div>
  );
}

export default function GuidePage() {
  return (
    <div className="lp-page">

      <div className="lp-hero">
        <div className="lp-hero-tag">📖 Guide</div>
        <h1>How Learnlia Works</h1>
        <p>
          Everything you need to get started — from adding your first course
          to completing a full learning roadmap.
        </p>
      </div>

      <div className="lp-container lp-container--wide">

        {/* ── 1. Learning Tracks ── */}
        <div className="lp-section">
          <h2>1. Learning Tracks (New in V2)</h2>
          <p>
            Tracks are highly structured, curated curriculums designed by experts to guide you from zero to job-ready. Instead of wandering through random videos, a Track provides a clear, step-by-step path broken down into Phases, AI Assessments, and Capstone Projects.
          </p>
          <ul>
            <li>Curated progression: Beginner to Advanced.</li>
            <li>Enforce learning with mandatory AI quizzes (70% to pass) to unlock next phases.</li>
            <li>Earn a verified Certificate of Completion automatically.</li>
            <li>Shows exactly how far along you are and predicts your timeline for mastery.</li>
          </ul>
        </div>

        {/* ── 2. Learnlia Intelligence ── */}
        <div className="lp-section">
          <h2>2. Learnlia Intelligence Suggestions</h2>
          <p>
            As soon as you tell Learnlia your primary goal and target role (e.g. "Become a Full Stack Developer by October"), our AI suggestion engine automatically curates the perfect Learning Tracks for you, surfacing them directly on your Dashboard.
          </p>
        </div>

        {/* ── 3. Dashboard Overview ── */}
        <div className="lp-section">
          <h2>3. Dashboard — Your Learning Overview</h2>
          <p>
            The dashboard gives you a snapshot of everything — your AI Track suggestions, current streak,
            active courses, roadmap progress, and a Continue Learning card that takes
            you straight back to where you left off.
          </p>
          <GuideImage
            src="./dashboard.png"
            alt="Learnlia dashboard"
            caption="Your dashboard shows everything you need to continue learning"
          />
        </div>

        {/* ── 4. Add External Courses ── */}
        <div className="lp-section">
          <h2>4. Add External Courses from YouTube</h2>
          <p>
            Have a specific YouTube playlist you want to study? No problem. Paste any public YouTube playlist URL into Learnlia, and we'll import the videos, estimate completion times, and allow you to track your progress granularly.
          </p>
          <GuideImage
            src="./add-course.png"
            alt="Add a course from a YouTube playlist"
            caption="Paste a YouTube playlist URL to import a course instantly"
          />
        </div>

        {/* ── 5. Course Player & Notes ── */}
        <div className="lp-section">
          <h2>5. Course Player & Notes</h2>
          <p>
            Open your course and start watching. Learnlia tracks exactly
            how far into each video you've gone and marks lessons complete
            when you finish them. Your overall course percentage updates in real time.
          </p>
          <GuideImage
            src="./player.png"
            alt="Course player with progress tracking"
            caption="The course player shows your progress and lets you take notes while watching"
          />
        </div>

        {/* ── 6. Study Planner ── */}
        <div className="lp-section">
          <h2>6. Dynamic Study Planner</h2>
          <p>
            Every course has a built-in Study Planner. Pick a target — 7, 14,
            30 days or custom — and get a personalized daily watch target.
            It uses your actual remaining content and your real 14-day average
            pace to give you a realistic pacing plan.
          </p>
          <GuideImage
            src="./study-planner.png"
            alt="Study Planner"
            caption="The Study Planner calculates your daily target and shows your estimated finish date"
          />
        </div>

        {/* ── 7. Custom Learning Roadmaps ── */}
        <div className="lp-section">
          <h2>7. Custom Learning Roadmaps</h2>
          <p>
            Instead of learning randomly, let YOUR goals drive the plan. Roadmaps let you generate custom step-by-step career path visuals and populate them with wherever independent courses or projects you prefer. Connect them in a visual graph to chart your own path!
          </p>
          <GuideImage
            src="./Roadmap.png"
            alt="Learning roadmap visualization"
            caption="Build a visual roadmap and follow it step by step"
          />
        </div>

        {/* ── 8. Roadmap Projects ── */}
        <div className="lp-section">
          <h2>8. Projects</h2>
          <p>
            Roadmaps include project nodes where you apply what you've learned.
            Each project has a title, description, tech stack, and links to
            your GitHub and live demo. Mark it complete to unlock the next step.
          </p>
          <GuideImage
            src="./project.png"
            alt="Project step"
            caption="Projects sit alongside courses in your roadmap — complete them to advance"
          />
        </div>

        {/* ── 9. AI Assessments & Proof Profiles ── */}
        <div className="lp-section">
          <h2>9. AI Assessments & Shareable Proof Profiles</h2>
          <p>
            True mastery requires testing what you've learned. In Learning Tracks, you'll encounter AI-powered Assessment quizzes uniquely generated for the curriculum. 
            When you complete a Learning Track, Learnlia automatically generates a Public Proof Profile for you. This acts as an interactive resume showcasing your Track Readiness Score, completed phases, quiz scores, and links to the Github repos of your submitted projects.
          </p>
        </div>

        {/* ── 10. Streaks, XP, & Analytics ── */}
        <div className="lp-section">
          <h2>10. Streaks, XP, & Analytics</h2>
          <p>
            Consistency is built through daily habits. We track your streak every day you push your knowledge forward. Earn XP, level up, and monitor your 90-day retention heatmap right from the Dashboard. PRO users unlock exclusive insight modules tracking velocity and comparative rank algorithms.
          </p>
          <GuideImage
            src="./streak.png"
            alt="Streak system"
            caption="Your streak card shows today's progress toward your daily goal"
          />
          <br/>
          <GuideImage
            src="./analytics.png"
            alt="Learning analytics"
            caption="Your focus trend and heatmap reveal real patterns in your learning habits"
          />
        </div>

        {/* ── Philosophy ── */}
        <div className="lp-section" style={{ background: "var(--accent-light, rgba(37,99,235,0.05))", borderRadius: "12px", padding: "28px", border: "1px solid rgba(37,99,235,0.12)", borderBottom: "1px solid rgba(37,99,235,0.12)" }}>
          <h2 style={{ marginBottom: "10px" }}>The Learnlia Philosophy</h2>
          <p>
            Small consistent progress compounds into mastery. You don't need
            long study sessions — you need to show up every day. Learnlia is
            designed to make that as frictionless as possible.
          </p>
          <p>
            The best learners aren't the most talented. They're the most consistent.
          </p>
        </div>

      </div>
    </div>
  );
}